/* StageGuardModal — manage validation guards on a specific stage
   Opened from the Pipeline Management stage row via the 🛡️ button.
   No pipeline/stage inputs needed — context is passed as props. */
import { useState } from 'react';
import css from '../AdminPage.module.css';
import {
  useStageGuardsByStage,
  useCreateStageGuard,
  useUpdateStageGuard,
  useDeleteStageGuard,
  useFieldDefinitions,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { StageGuard } from '../../../types/admin';
import {
  OPERATORS,
  GUARD_TYPES,
  getOperatorsForType,
  getInputTypeForField,
  describeGuard,
  type GuardOperator,
  type GuardType,
} from '../../../constants/guardOperators';

interface Props {
  stageId: number;
  stageName: string;
  caseTypeName: string;
  onClose: () => void;
}

export default function StageGuardModal({ stageId, stageName, caseTypeName, onClose }: Props) {
  const { data: guards = [], isLoading } = useStageGuardsByStage(stageId);
  const { data: fieldDefs = [] } = useFieldDefinitions();
  const createGuard = useCreateStageGuard();
  const updateGuard = useUpdateStageGuard();
  const deleteGuard = useDeleteStageGuard();
  const { showToast } = useToast();

  /* ── Form visibility ── */
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<StageGuard | null>(null);

  /* ── Form state ── */
  const [guardType, setGuardType] = useState<GuardType>('field_check');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('');
  const [operator, setOperator] = useState<string>('is_not_empty');
  const [value, setValue] = useState('');
  const [value2, setValue2] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isActive, setIsActive] = useState(true);

  /* All fields from field_definitions — includes both column-mode and EAV fields.
     Backend now joins olap.case_fields to support EAV field validation. */
  const caseFields = fieldDefs;

  const operators = getOperatorsForType(fieldType || 'string');
  const operatorDef = OPERATORS[operator as GuardOperator];
  const inputType = getInputTypeForField(fieldType);

  /* ── Open handlers ── */
  function openCreate() {
    setEditing(null);
    setGuardType('field_check');
    setFieldKey('');
    setFieldName('');
    setFieldType('');
    setOperator('is_not_empty');
    setValue('');
    setValue2('');
    setErrorMessage('');
    setIsActive(true);
    setShowForm(true);
  }

  function openEdit(g: StageGuard) {
    setEditing(g);
    setGuardType(g.guard_type as GuardType);
    const key = g.guard_config.field_key || g.guard_config.field_column || '';
    setFieldKey(key);
    setFieldName(g.guard_config.field_name || '');
    setFieldType(g.guard_config.field_type || '');
    setOperator(g.guard_config.operator || 'is_not_empty');
    setValue(g.guard_config.value || '');
    setValue2(g.guard_config.value2 || '');
    setErrorMessage(g.error_message || '');
    setIsActive(g.is_active);
    setShowForm(true);
  }

  /* ── Field selector ── */
  function handleFieldSelect(e: React.ChangeEvent<HTMLSelectElement>) {
    const key = e.target.value;
    if (!key) {
      setFieldKey('');
      setFieldName('');
      setFieldType('');
      return;
    }
    const def = caseFields.find((f) => f.key === key);
    if (def) {
      setFieldKey(def.key.toLowerCase());
      setFieldName(def.name);
      const ft = (def.type || def.field_type || 'string').toLowerCase();
      setFieldType(ft);
      // Reset operator to first valid for new type
      const ops = getOperatorsForType(ft);
      setOperator(ops[0]);
      setValue('');
      setValue2('');
    }
  }

  /* ── Save ── */
  async function save() {
    if (guardType === 'field_check' && !fieldKey) {
      showToast('Please select a field to validate', 'error');
      return;
    }
    const payload: Partial<StageGuard> = {
      stage_id: stageId,
      guard_type: guardType,
      guard_config: {
        field_key: fieldKey,
        field_name: fieldName,
        field_type: fieldType,
        operator,
        value: operatorDef?.needsValue ? value || null : null,
        value2: operatorDef?.needsSecondValue ? value2 || null : null,
      },
      error_message: errorMessage || null,
      is_active: isActive,
    };
    try {
      if (editing) {
        await updateGuard.mutateAsync({ guard_id: editing.guard_id, ...payload });
        showToast('Guard updated', 'success');
      } else {
        await createGuard.mutateAsync(payload);
        showToast('Guard created', 'success');
      }
      setShowForm(false);
    } catch {
      showToast(editing ? 'Failed to update guard' : 'Failed to create guard', 'error');
    }
  }

  /* ── Delete ── */
  async function remove(g: StageGuard) {
    const label =
      g.guard_config.field_name ||
      g.guard_config.field_key ||
      g.guard_config.field_column ||
      'this guard';
    if (
      !window.confirm(
        `Delete the guard for "${label}" on stage "${stageName}"?\n\nThis cannot be undone.`,
      )
    )
      return;
    try {
      await deleteGuard.mutateAsync(g.guard_id);
      showToast('Guard deleted', 'success');
    } catch {
      showToast('Failed to delete guard', 'error');
    }
  }

  /* ── Toggle active / inactive ── */
  async function toggleActive(g: StageGuard) {
    try {
      await updateGuard.mutateAsync({
        guard_id: g.guard_id,
        stage_id: g.stage_id,
        guard_type: g.guard_type,
        guard_config: g.guard_config,
        error_message: g.error_message ?? null,
        is_active: !g.is_active,
      });
      showToast(g.is_active ? 'Guard deactivated' : 'Guard activated', 'success');
    } catch {
      showToast('Failed to update guard', 'error');
    }
  }

  /* ── Current field key for the select (used when editing) ── */
  const selectedFieldKey = caseFields.find((f) => f.key.toLowerCase() === fieldKey)?.key ?? '';

  return (
    <div className={css.modalBackdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={css.modal}>
        <div className={css.modalHeader}>
          <div>
            <h3 className={css.modalTitle}>🛡️ Stage Guards</h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
              {caseTypeName} → <strong>{stageName}</strong>
            </div>
          </div>
          <button className={css.modalClose} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={css.modalBody}>
          {/* ── Guard list ── */}
          {isLoading ? (
            <div className={css.spinner}>
              <div className={css.spinnerDot} />
              <div className={css.spinnerDot} />
              <div className={css.spinnerDot} />
            </div>
          ) : guards.length === 0 && !showForm ? (
            <div className={css.emptyState}>
              <div className={css.emptyIcon}>🛡️</div>
              <p>No guards on this stage yet.</p>
              <p className={css.emptyMessage}>
                Guards block cases from advancing until conditions are met.
              </p>
            </div>
          ) : (
            guards.map((g) => (
              <div
                key={g.guard_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 8,
                  opacity: g.is_active ? 1 : 0.6,
                }}
              >
                <div style={{ flex: 1, fontSize: '0.88rem' }}>
                  <div style={{ fontWeight: 500 }}>{describeGuard(g.guard_config)}</div>
                  {g.error_message && (
                    <div
                      style={{ fontSize: '0.77rem', color: 'var(--text-tertiary)', marginTop: 2 }}
                    >
                      💬 {g.error_message}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => toggleActive(g)}
                  style={{
                    cursor: 'pointer',
                    border: 'none',
                    borderRadius: 12,
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: g.is_active ? 'var(--success-100, #dcfce7)' : 'var(--gray-100)',
                    color: g.is_active ? 'var(--success-700, #15803d)' : 'var(--gray-500)',
                  }}
                  title={g.is_active ? 'Click to deactivate' : 'Click to activate'}
                >
                  {g.is_active ? 'Active' : 'Inactive'}
                </button>
                <button className={css.btnSmall} onClick={() => openEdit(g)}>
                  Edit
                </button>
                <button
                  className={css.btnDanger}
                  style={{ padding: '2px 10px', fontSize: '0.78rem' }}
                  onClick={() => remove(g)}
                >
                  Delete
                </button>
              </div>
            ))
          )}

          {/* ── Guard form (create or edit) ── */}
          {showForm && (
            <div
              style={{
                border: '1px solid var(--primary-300, #86efac)',
                borderRadius: 8,
                padding: 16,
                marginTop: guards.length > 0 ? 12 : 0,
                background: 'var(--primary-50, #f0fdf4)',
              }}
            >
              <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 600 }}>
                {editing ? '✏️ Edit Guard' : '➕ New Guard'}
              </h4>

              {/* Guard type */}
              <label className={css.formLabel}>
                Guard Type
                <select
                  className={css.formSelect}
                  value={guardType}
                  onChange={(e) => setGuardType(e.target.value as GuardType)}
                >
                  {GUARD_TYPES.map((gt) => (
                    <option key={gt.value} value={gt.value} disabled={!gt.available}>
                      {gt.label}
                      {!gt.available ? ' (coming soon)' : ''}
                    </option>
                  ))}
                </select>
              </label>

              {guardType === 'field_check' && (
                <>
                  {/* Field selector */}
                  <label className={css.formLabel} style={{ marginTop: 10 }}>
                    Field to Validate
                    <select
                      className={css.formSelect}
                      value={selectedFieldKey}
                      onChange={handleFieldSelect}
                    >
                      <option value="">— Select a field —</option>
                      {caseFields.map((f) => (
                        <option key={f.id} value={f.key}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                    {fieldKey && (
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-tertiary)',
                          marginTop: 2,
                          display: 'block',
                        }}
                      >
                        Column: <code>{fieldKey}</code>
                        {fieldType ? ` · Type: ${fieldType}` : ''}
                      </span>
                    )}
                  </label>

                  {/* Condition / operator selector */}
                  {fieldKey && (
                    <label className={css.formLabel} style={{ marginTop: 10 }}>
                      Condition
                      <select
                        className={css.formSelect}
                        value={operator}
                        onChange={(e) => {
                          setOperator(e.target.value);
                          setValue('');
                          setValue2('');
                        }}
                      >
                        {operators.map((op) => (
                          <option key={op} value={op}>
                            {OPERATORS[op]?.label ?? op}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {/* Value input */}
                  {operatorDef?.needsValue && (
                    <label className={css.formLabel} style={{ marginTop: 10 }}>
                      Value
                      <input
                        className={css.formInput}
                        type={inputType}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Enter value to compare against"
                      />
                    </label>
                  )}

                  {/* Second value (between) */}
                  {operatorDef?.needsSecondValue && (
                    <label className={css.formLabel} style={{ marginTop: 10 }}>
                      Upper Bound
                      <input
                        className={css.formInput}
                        type={inputType}
                        value={value2}
                        onChange={(e) => setValue2(e.target.value)}
                        placeholder="Upper bound"
                      />
                    </label>
                  )}
                </>
              )}

              {/* Custom error message */}
              <label className={css.formLabel} style={{ marginTop: 10 }}>
                Custom Error Message{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optional)</span>
                <input
                  className={css.formInput}
                  value={errorMessage}
                  onChange={(e) => setErrorMessage(e.target.value)}
                  placeholder="Shown to users when blocked — leave blank for auto-generated message"
                />
              </label>

              {/* Active toggle (edit mode) */}
              {editing && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 10,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  Active
                </label>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className={css.btnSecondary} onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button
                  className={css.btnPrimary}
                  onClick={save}
                  disabled={createGuard.isPending || updateGuard.isPending}
                >
                  {editing ? 'Save Changes' : 'Add Guard'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={css.modalFooter}>
          {!showForm && (
            <button className={css.btnPrimary} onClick={openCreate}>
              + Add Guard
            </button>
          )}
          <button className={css.btnSecondary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
